import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { stripHtml as sanitizeStripHtml } from '../../utils/sanitize';
import {
  fetchAllKpiDrilldownTickets,
  fetchKpiDrilldownTickets,
  type KpiDrilldownSortBy,
  type KpiDrilldownSlaCellDto,
  type KpiDrilldownTicketDto,
  type SlaViewBucketKey,
  type SlaViewTargetScope,
} from './kpiDashboardApi';
import {
  KPI_DASHBOARD_TABLE_TD,
  KPI_DASHBOARD_TABLE_TD_PRIMARY,
  KPI_DASHBOARD_TABLE_TH,
  kpiDashboardTableRowClass,
} from './kpiDashboardTableStyles';
import { useDateFormatter } from '../../hooks/useDateFormatter';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
/** Ticket ID, Type, Short Description, Assignment Group, Company, State, Priority, Created, Last Updated Time, Assigned To, Last Updated By */
const BASE_COL_COUNT = 11;

/**
 * SLA group tints (same family as UnifiedPulseReportTable).
 * Laps + Remaining for a type share one color; each SLA type differs.
 * Headers omit KPI_DASHBOARD_TABLE_TH sky bg so these tints actually apply.
 */
const SLA_TH_BASE =
  'sticky top-0 z-10 border border-slate-200 px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-800 whitespace-nowrap sm:text-xs';
const SLA_COL = {
  resolution: { th: `${SLA_TH_BASE} bg-green-200`, td: 'bg-green-100' },
  ongoing: { th: `${SLA_TH_BASE} bg-purple-200`, td: 'bg-purple-100' },
  response: { th: `${SLA_TH_BASE} bg-blue-200`, td: 'bg-blue-100' },
} as const;

export type SlaViewOrganizeBy = 'team' | 'customer';
export type SlaViewPieTicketType = 'CASE' | 'INCIDENT' | 'SERVICE_REQUEST';

/** Case → Response only; Incident/SR → Resolution + Ongoing (no Response). */
function slaColumnsForTicketType(ticketType: SlaViewPieTicketType): {
  showResponse: boolean;
  showResolution: boolean;
  showOngoing: boolean;
} {
  if (ticketType === 'CASE') {
    return { showResponse: true, showResolution: false, showOngoing: false };
  }
  return { showResponse: false, showResolution: true, showOngoing: true };
}

function ticketDetailPath(t: KpiDrilldownTicketDto): string {
  switch (t.ticketType) {
    case 'INCIDENT':
      return `/incidents/${t.id}`;
    case 'SERVICE_REQUEST':
      return `/service-requests/${t.id}`;
    case 'CASE':
      return `/cases/${t.id}`;
    case 'CHANGE':
      return `/changes/${t.id}`;
    case 'PROBLEM':
      return `/problems/${t.id}`;
    default:
      return `/tickets/${t.id}`;
  }
}

function openTicketRow(t: KpiDrilldownTicketDto) {
  if (!t?.id) return;
  window.open(ticketDetailPath(t), '_blank', 'noopener,noreferrer');
}

function teamLabel(t: KpiDrilldownTicketDto): string {
  return t.team || t.assignmentGroup || '—';
}

function customerLabel(t: KpiDrilldownTicketDto): string {
  return t.company || '—';
}

function organizeKey(t: KpiDrilldownTicketDto, organizeBy: SlaViewOrganizeBy): string {
  return organizeBy === 'customer' ? customerLabel(t) : teamLabel(t);
}

function formatPct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`;
}

function slaLaps(sla: KpiDrilldownSlaCellDto): string {
  if (!sla || sla.status === 'N/A') return '—';
  if (sla.completedPct != null) return formatPct(sla.completedPct);
  if (sla.status === 'BREACHED') return '100%';
  return '—';
}

function slaRemaining(sla: KpiDrilldownSlaCellDto): string {
  if (!sla || sla.status === 'N/A') return '—';
  if (sla.remainingPct != null) return formatPct(sla.remainingPct);
  if (sla.completedPct != null) return formatPct(Math.round((100 - sla.completedPct) * 10) / 10);
  if (sla.status === 'BREACHED') return '0%';
  return '—';
}

function buildSlaViewCsv(
  tickets: KpiDrilldownTicketDto[],
  cols: { showResponse: boolean; showResolution: boolean; showOngoing: boolean },
  fileSuffix: string,
  formatDateTime: (v: string | null | undefined) => string
): { blob: Blob; fileName: string } {
  const headers = [
    'Ticket ID',
    'Ticket Type',
    'Short Description',
    'Assignment Group',
    'Company',
    'State',
    'Priority',
    'Ticket Created',
    'Last Updated Time',
    'Assigned To',
    'Last Updated By',
    ...(cols.showResolution
      ? ['Resolution SLA - Laps', 'Resolution SLA - Remaining']
      : []),
    ...(cols.showOngoing
      ? ['Ongoing Communication SLA - Laps', 'Ongoing Communication SLA - Remaining']
      : []),
    ...(cols.showResponse ? ['Response SLA - Laps', 'Response SLA - Remaining'] : []),
  ];

  const rows = tickets.map((t) => {
    const desc = t.shortDescription
      ? sanitizeStripHtml(t.shortDescription).replace(/\s+/g, ' ').trim()
      : '';
    const cells: string[] = [
      t.number,
      t.taskTypeLabel,
      desc || '—',
      teamLabel(t),
      customerLabel(t),
      t.stateLabel,
      t.priorityLabel || t.priority || '—',
      formatDateTime(t.ticketCreated || t.createdAt),
      formatDateTime(t.ticketLastUpdatedTime),
      t.assignedTo || '—',
      t.lastUpdatedBy || '—',
    ];
    if (cols.showResolution) {
      cells.push(slaLaps(t.resolutionSla), slaRemaining(t.resolutionSla));
    }
    if (cols.showOngoing) {
      cells.push(slaLaps(t.ongoingSla), slaRemaining(t.ongoingSla));
    }
    if (cols.showResponse) {
      cells.push(slaLaps(t.responseSla), slaRemaining(t.responseSla));
    }
    return cells;
  });

  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ),
  ].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const stamp = new Date().toISOString().slice(0, 10);
  return { blob, fileName: `kpi_sla_view_${fileSuffix}_${stamp}.csv` };
}

export interface KpiSlaTicketsTableProps {
  title: string;
  organizeBy: SlaViewOrganizeBy;
  filterValues: Record<string, string | string[]>;
  filterKey: string;
  ticketType: SlaViewPieTicketType;
  targetScope?: SlaViewTargetScope;
  bucket: SlaViewBucketKey;
  /** When set, table uses Dashboard drill overlay chrome (Back to charts / close). */
  onClose?: () => void;
  backButtonLabel?: string;
}

const KpiSlaTicketsTable: React.FC<KpiSlaTicketsTableProps> = ({
  title,
  organizeBy,
  filterValues,
  filterKey,
  ticketType,
  targetScope = 'ANY',
  bucket,
  onClose,
  backButtonLabel = 'Back to charts',
}) => {
  const { formatDateTime: formatDateTimeBase } = useDateFormatter();
  const formatDateTime = useCallback(
    (value: string | null | undefined) => formatDateTimeBase(value),
    [formatDateTimeBase]
  );
  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [exportingCsv, setExportingCsv] = useState(false);

  const slaCols = useMemo(() => slaColumnsForTicketType(ticketType), [ticketType]);
  const tableColCount =
    BASE_COL_COUNT +
    (slaCols.showResolution ? 2 : 0) +
    (slaCols.showOngoing ? 2 : 0) +
    (slaCols.showResponse ? 2 : 0);

  const drillSpec = useMemo(
    () =>
      ({
        kind: 'sla_view_bucket' as const,
        ticketType,
        bucket,
        targetScope,
      }),
    [ticketType, bucket, targetScope]
  );

  const sortBy: KpiDrilldownSortBy = organizeBy;

  const queryKey = useMemo(
    () => [
      'kpi-sla-view-drill',
      filterKey,
      organizeBy,
      ticketType,
      targetScope,
      bucket,
      page,
      pageSize,
    ],
    [filterKey, organizeBy, ticketType, targetScope, bucket, page, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [filterKey, organizeBy, ticketType, targetScope, bucket, pageSize]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchKpiDrilldownTickets(
        filterValues,
        timezoneOffset,
        drillSpec,
        page,
        pageSize,
        null,
        sortBy
      ),
    staleTime: 30_000,
  });

  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const th = (extra = '') => `${KPI_DASHBOARD_TABLE_TH} ${extra}`.trim();
  const td = (extra = '') => `${KPI_DASHBOARD_TABLE_TD} ${extra}`.trim();

  const handleExportCsv = useCallback(async () => {
    if (total === 0 || exportingCsv) return;
    setExportingCsv(true);
    try {
      const tickets = await fetchAllKpiDrilldownTickets(
        filterValues,
        timezoneOffset,
        drillSpec,
        null
      );
      if (tickets.length === 0) {
        toast.error('No tickets to export for the current selection.');
        return;
      }
      const { blob, fileName } = buildSlaViewCsv(
        tickets,
        slaCols,
        `${ticketType.toLowerCase()}_${targetScope.toLowerCase()}_${bucket}`,
        formatDateTime
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${tickets.length.toLocaleString()} row(s) to CSV.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'CSV export failed.');
    } finally {
      setExportingCsv(false);
    }
  }, [
    bucket,
    drillSpec,
    exportingCsv,
    filterValues,
    formatDateTime,
    slaCols,
    targetScope,
    ticketType,
    timezoneOffset,
    total,
  ]);

  let lastOrganize: string | null = null;
  const isOverlay = Boolean(onClose);

  return (
    <section
      role={isOverlay ? 'dialog' : undefined}
      aria-modal={isOverlay ? true : undefined}
      aria-label="SLA View tickets"
      className={
        isOverlay
          ? 'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-lg shadow-slate-900/5 ring-1 ring-slate-950/[0.04]'
          : 'relative flex min-h-0 flex-1 flex-col'
      }
    >
      <div
        className={
          isOverlay
            ? 'flex shrink-0 flex-col gap-3 border-b border-gray-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between'
            : 'flex shrink-0 items-center justify-between gap-2 border-b border-l-4 border-slate-100 border-l-indigo-500 bg-gradient-to-r from-slate-50 via-white to-indigo-50/40 py-2 pl-[13px] pr-2 sm:pl-[17px] sm:pr-3'
        }
      >
        {isOverlay ? (
          <>
            <div className="min-w-0">
              <h3 className="text-lg font-medium text-gray-900">
                Showing: {title}
                {total > 0 ? ` (${total.toLocaleString()} tickets)` : !isLoading ? ' (0 tickets)' : ''}
              </h3>
              <p className="mt-0.5 text-xs text-gray-500">
                {ticketType === 'CASE'
                  ? 'Response SLA Laps / Remaining. Sorted by '
                  : 'Resolution and Ongoing Communication SLA Laps / Remaining. Sorted by '}
                {organizeBy === 'team' ? 'Team' : 'Customer'}.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {isFetching ? (
                <span
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600"
                  aria-live="polite"
                >
                  <ArrowPathIcon className="h-3 w-3 animate-spin" aria-hidden />
                  Updating
                </span>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                <ArrowLeftIcon className="mr-1.5 h-4 w-4" aria-hidden />
                {backButtonLabel}
              </button>
              <button
                type="button"
                onClick={() => void handleExportCsv()}
                disabled={total === 0 || exportingCsv || isLoading}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 sm:px-3 sm:py-1.5 sm:text-sm"
              >
                <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0 text-slate-600 sm:h-4 sm:w-4" aria-hidden />
                {exportingCsv ? 'Exporting…' : 'Export CSV'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-1 hover:bg-gray-100"
                aria-label="Close drill-down"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="min-w-0 text-[11px] font-semibold uppercase tracking-wide text-slate-700 sm:text-xs">
              {title}
              {total > 0 ? (
                <span className="ml-2 font-normal normal-case tracking-normal text-slate-500">
                  ({total.toLocaleString()} tickets)
                </span>
              ) : null}
            </h3>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {isFetching ? (
                <span
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600"
                  aria-live="polite"
                >
                  <ArrowPathIcon className="h-3 w-3 animate-spin" aria-hidden />
                  Updating
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => void handleExportCsv()}
                disabled={total === 0 || exportingCsv || isLoading}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 sm:text-xs disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0 text-slate-600" aria-hidden />
                {exportingCsv ? 'Exporting…' : 'Export CSV'}
              </button>
            </div>
          </>
        )}
      </div>

      <div
        className={
          isOverlay
            ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
            : 'flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm shadow-slate-900/5 ring-1 ring-slate-950/[0.04]'
        }
      >

        {isLoading && (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-slate-500">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
            <p className="mt-3 text-sm">Loading tickets…</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="flex shrink-0 items-start gap-2 border-b border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden />
            <span>{error instanceof Error ? error.message : 'Failed to load tickets.'}</span>
          </div>
        )}

        {data && !isLoading && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="min-w-max w-full border-collapse text-xs sm:text-sm">
                <thead>
                  <tr>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Ticket ID
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Ticket Type
                    </th>
                    <th className={th('min-w-[200px]')} scope="col">
                      Short Description
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Assignment Group
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Company
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      State
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Priority
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Ticket Created
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Last Updated Time
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Assigned To
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Last Updated By
                    </th>
                    {slaCols.showResolution ? (
                      <>
                        <th className={SLA_COL.resolution.th} scope="col">
                          Resolution SLA - Laps
                        </th>
                        <th className={SLA_COL.resolution.th} scope="col">
                          Resolution SLA - Remaining
                        </th>
                      </>
                    ) : null}
                    {slaCols.showOngoing ? (
                      <>
                        <th className={SLA_COL.ongoing.th} scope="col">
                          Ongoing Communication SLA - Laps
                        </th>
                        <th className={SLA_COL.ongoing.th} scope="col">
                          Ongoing Communication SLA - Remaining
                        </th>
                      </>
                    ) : null}
                    {slaCols.showResponse ? (
                      <>
                        <th className={SLA_COL.response.th} scope="col">
                          Response SLA - Laps
                        </th>
                        <th className={SLA_COL.response.th} scope="col">
                          Response SLA - Remaining
                        </th>
                      </>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {data.tickets.length === 0 ? (
                    <tr>
                      <td
                        colSpan={tableColCount}
                        className="border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500"
                      >
                        No tickets match the selected SLA bucket.
                      </td>
                    </tr>
                  ) : (
                    data.tickets.map((t, i) => {
                      const descRaw = t.shortDescription
                        ? sanitizeStripHtml(t.shortDescription)
                        : '';
                      const desc = descRaw.replace(/\s+/g, ' ').trim() || '—';
                      const org = organizeKey(t, organizeBy);
                      const showSection = org !== lastOrganize;
                      lastOrganize = org;

                      return (
                        <React.Fragment key={t.id}>
                          {showSection ? (
                            <tr className="bg-slate-100/90">
                              <td
                                colSpan={tableColCount}
                                className="border border-slate-200 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-700"
                              >
                                {organizeBy === 'customer' ? 'Customer' : 'Team'}: {org}
                              </td>
                            </tr>
                          ) : null}
                          <tr
                            className={`cursor-pointer ${kpiDashboardTableRowClass(i)}`}
                            onClick={() => openTicketRow(t)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openTicketRow(t);
                              }
                            }}
                            tabIndex={0}
                            role="link"
                            aria-label={`Open ticket ${t.number}`}
                            data-ticket-id={t.id}
                          >
                            <td className={`${KPI_DASHBOARD_TABLE_TD_PRIMARY} whitespace-nowrap`}>
                              {t.number}
                            </td>
                            <td className={td('whitespace-nowrap')}>{t.taskTypeLabel}</td>
                            <td className={td('max-w-[320px]')}>
                              <div className="truncate" title={desc}>
                                {desc}
                              </div>
                            </td>
                            <td className={td('whitespace-nowrap')}>{teamLabel(t)}</td>
                            <td className={td('max-w-[180px]')}>
                              <div className="truncate" title={customerLabel(t)}>
                                {customerLabel(t)}
                              </div>
                            </td>
                            <td className={td('whitespace-nowrap')}>{t.stateLabel}</td>
                            <td className={td('whitespace-nowrap')}>
                              {t.priorityLabel || t.priority || '—'}
                            </td>
                            <td className={td('whitespace-nowrap')}>
                              {formatDateTime(t.ticketCreated || t.createdAt)}
                            </td>
                            <td className={td('whitespace-nowrap')}>
                              {formatDateTime(t.ticketLastUpdatedTime)}
                            </td>
                            <td className={td('whitespace-nowrap')}>{t.assignedTo || '—'}</td>
                            <td className={td('whitespace-nowrap')}>{t.lastUpdatedBy || '—'}</td>
                            {slaCols.showResolution ? (
                              <>
                                <td
                                  className={td(
                                    `whitespace-nowrap tabular-nums ${SLA_COL.resolution.td}`
                                  )}
                                >
                                  {slaLaps(t.resolutionSla)}
                                </td>
                                <td
                                  className={td(
                                    `whitespace-nowrap tabular-nums ${SLA_COL.resolution.td}`
                                  )}
                                >
                                  {slaRemaining(t.resolutionSla)}
                                </td>
                              </>
                            ) : null}
                            {slaCols.showOngoing ? (
                              <>
                                <td
                                  className={td(
                                    `whitespace-nowrap tabular-nums ${SLA_COL.ongoing.td}`
                                  )}
                                >
                                  {slaLaps(t.ongoingSla)}
                                </td>
                                <td
                                  className={td(
                                    `whitespace-nowrap tabular-nums ${SLA_COL.ongoing.td}`
                                  )}
                                >
                                  {slaRemaining(t.ongoingSla)}
                                </td>
                              </>
                            ) : null}
                            {slaCols.showResponse ? (
                              <>
                                <td
                                  className={td(
                                    `whitespace-nowrap tabular-nums ${SLA_COL.response.td}`
                                  )}
                                >
                                  {slaLaps(t.responseSla)}
                                </td>
                                <td
                                  className={td(
                                    `whitespace-nowrap tabular-nums ${SLA_COL.response.td}`
                                  )}
                                >
                                  {slaRemaining(t.responseSla)}
                                </td>
                              </>
                            ) : null}
                          </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50/80 px-3 py-2">
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <span>Rows per page</span>
                <select
                  className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-xs"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span>
                  Page {totalPages === 0 ? 0 : page} of {totalPages}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={totalPages === 0 || page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default KpiSlaTicketsTable;
